import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { onboardingApi, type GuideResponse, type UpdateGuideStepInput } from '@/shared/api/onboarding';
import { queryKeys } from '@/shared/api/queryKeys';

/**
 * Fetches the canton-personalized Setup Guide context + progress for the given role.
 * The role usually comes from the auth store; passing it keeps the cache correct for
 * dual-role accounts.
 */
export function useGuideContext(role?: 'employer' | 'employee') {
  return useQuery({
    queryKey: queryKeys.onboarding.guide(role),
    queryFn: () => onboardingApi.getGuide(role),
    staleTime: 60_000,
  });
}

/**
 * Marks a manual acknowledgement step done/undone, or dismisses/completes the guide.
 * Optimistically patches the cached progress so checkboxes feel instant.
 */
export function useMarkGuideStep(role?: 'employer' | 'employee') {
  const qc = useQueryClient();
  const key = queryKeys.onboarding.guide(role);

  return useMutation({
    mutationFn: (input: UpdateGuideStepInput) => onboardingApi.updateStep(input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<GuideResponse>(key);
      if (prev) {
        const completed = new Set(prev.progress.completedSteps);
        if (input.stepId) {
          if (input.done === false) completed.delete(input.stepId);
          else completed.add(input.stepId);
        }
        qc.setQueryData<GuideResponse>(key, {
          ...prev,
          progress: {
            ...prev.progress,
            completedSteps: Array.from(completed),
            dismissedAt:
              input.dismissed === true
                ? new Date().toISOString()
                : input.dismissed === false
                  ? null
                  : prev.progress.dismissedAt,
          },
        });
      }
      return { prev };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
    },
  });
}
